import { type FC, type FormEvent, useState } from "react";
import {
  Alert,
  AppBar,
  Button,
  Card,
  CardContent,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import {
  ProductsAutocomplete,
  formatCurrency,
} from "@/partner/features/products";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import {
  PromocodeTypeType,
  type CreatePromocodeInputType,
  type ProductCurrencyType,
} from "@/types/graphql/partner.generated";
import { PromocodeCampaignsAutocomplete } from "./PromocodeCampaignsAutocomplete";

const FORM_SPACING = 2;

// Тумблер типа промокода — выбранная кнопка закрашена цветом primary
// (référence: PromocodeForm.js, аналог ProductTypeToggle, но по брифу задачи
// стилизуем inline sx, без styled()).
const SELECTED_TOGGLE_SX = {
  "&.Mui-selected": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
  },
} as const;

export interface PromocodeFormValues {
  title: string;
  code: string;
  productIds: string[];
  limit: string;
  discount: string;
  amount: string;
  type: PromocodeTypeType;
  promocodeCampaignId: string | null;
  promocodeCampaignTitle: string;
  createNewCampaign: boolean;
  trialPeriod: string;
  startAt: moment.Moment | null;
  finishAt: moment.Moment | null;
}

export interface PromocodeFormMetrics {
  activations: number;
  totalRevenue: { value: number; currency: ProductCurrencyType };
}

interface PromocodeFormProps {
  /** Наличие initialValues определяет режим формы (isEdit) — отдельного
   * проп-флага брифом не предусмотрено (см. Task 8 report). */
  initialValues?: Partial<PromocodeFormValues>;
  loading: boolean;
  error?: string | null;
  /** Диалог сам решает, что делать с code/type: create — передаёт как есть
   * (input уже CreatePromocodeInputType), edit — вырезает оба поля перед
   * updatePromocode (UpdatePromocodeInputType их не содержит). */
  onSubmit: (input: CreatePromocodeInputType) => void;
  submitLabel: string;
  title: string;
  onClose: () => void;
  /** Только для edit — блок из двух метрик над формой, référence
   * DialogMetrics, показывается только на мобиле. */
  metrics?: PromocodeFormMetrics;
}

/**
 * Форма создания/редактирования промокода (référence: PromocodeForm.js).
 * Локальный useState по полю — référence не использует react-final-form, и
 * здесь форма живёт внутри диалога, который монтирует её заново на каждое
 * открытие (см. CreatePromocodeDialog/EditPromocodeDialog), так что
 * ре-синхронизация initialValues через useEffect не нужна.
 */
export const PromocodeForm: FC<PromocodeFormProps> = ({
  initialValues,
  loading,
  error,
  onSubmit,
  submitLabel,
  title: dialogTitle,
  onClose,
  metrics,
}) => {
  const { t } = useTranslation();
  const isMobile = useDeviceBreakpoint("md");
  const isEdit = Boolean(initialValues);

  const [title, setTitle] = useState(initialValues?.title ?? "");
  const [code, setCode] = useState(initialValues?.code ?? "");
  const [productIds, setProductIds] = useState<string[]>(
    initialValues?.productIds ?? []
  );
  const [limit, setLimit] = useState(initialValues?.limit ?? "");
  const [discount, setDiscount] = useState(initialValues?.discount ?? "");
  const [amount, setAmount] = useState(initialValues?.amount ?? "");
  const [type, setType] = useState<PromocodeTypeType>(
    initialValues?.type ?? PromocodeTypeType.DiscountType
  );
  const [promocodeCampaignId, setPromocodeCampaignId] = useState<string | null>(
    initialValues?.promocodeCampaignId ?? null
  );
  const [promocodeCampaignTitle, setPromocodeCampaignTitle] = useState(
    initialValues?.promocodeCampaignTitle ?? ""
  );
  const [createNewCampaign, setCreateNewCampaign] = useState(
    initialValues?.createNewCampaign ?? false
  );
  const [trialPeriod, setTrialPeriod] = useState(
    initialValues?.trialPeriod ?? ""
  );
  // Référence: дефолт при создании — «сейчас», при редактировании — из
  // данных (может отсутствовать → null).
  const [startAt, setStartAt] = useState<moment.Moment | null>(() =>
    isEdit ? (initialValues?.startAt ?? null) : moment()
  );
  const [finishAt, setFinishAt] = useState<moment.Moment | null>(
    initialValues?.finishAt ?? null
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({
      title,
      code,
      type,
      promocodeCampaignId: createNewCampaign ? null : promocodeCampaignId,
      promocodeCampaignTitle: createNewCampaign ? promocodeCampaignTitle : null,
      productIds,
      limit: limit === "" ? null : parseInt(limit, 10),
      discount: discount === "" ? null : parseFloat(discount),
      amount: amount === "" ? null : parseFloat(amount),
      trialPeriod: trialPeriod === "" ? null : parseInt(trialPeriod, 10),
      startAt: startAt?.toDate() ?? null,
      finishAt: finishAt?.toDate() ?? null,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <AppBar color="default" position="sticky" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            size="large"
            onClick={onClose}
            sx={{ mr: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 1 }}>
            {dialogTitle}
          </Typography>
          <Button
            variant="contained"
            type="submit"
            disabled={loading}
            disableElevation
            sx={{ ml: 2 }}
          >
            {submitLabel}
          </Button>
        </Toolbar>
        <Divider />
      </AppBar>

      <DialogContent sx={{ p: 1 }}>
        <Grid container spacing={1}>
          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {/* Référence: DialogMetrics — только при редактировании и только на
              мобиле. */}
          {metrics && isEdit && isMobile && (
            <Grid size={12}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 6, sm: 6, md: "grow", lg: "grow" }}>
                  <Card elevation={0} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      {t("promocodes.list.activationsColumn")}
                    </Typography>
                    <Typography variant="h6" color="textPrimary">
                      {metrics.activations}
                    </Typography>
                  </Card>
                </Grid>
                <Grid size={{ xs: 6, sm: 6, md: "grow", lg: "grow" }}>
                  <Card elevation={0} sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      {t("promocodes.list.revenueColumn")}
                    </Typography>
                    <Typography variant="h6" color="textPrimary">
                      {formatCurrency(
                        metrics.totalRevenue.value,
                        metrics.totalRevenue.currency
                      )}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          )}

          <Grid size={12}>
            <Card elevation={0}>
              <CardContent sx={{ pt: 3 }}>
                <Grid container spacing={FORM_SPACING}>
                  <Grid size={12}>
                    <TextField
                      autoFocus
                      required
                      fullWidth
                      label={t("promocodes.form.titleField")}
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Stack spacing={1}>
                      <PromocodeCampaignsAutocomplete
                        multiple={false}
                        disabled={createNewCampaign}
                        label={t("promocodes.form.campaign")}
                        value={promocodeCampaignId ? [promocodeCampaignId] : []}
                        onChange={(ids) =>
                          setPromocodeCampaignId(ids[0] ?? null)
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={createNewCampaign}
                            onChange={(event) =>
                              setCreateNewCampaign(event.target.checked)
                            }
                          />
                        }
                        label={t("promocodes.form.newCampaign")}
                      />
                      {createNewCampaign && (
                        <TextField
                          required
                          autoFocus
                          fullWidth
                          label={t("promocodes.form.newCampaignTitle")}
                          value={promocodeCampaignTitle}
                          onChange={(event) =>
                            setPromocodeCampaignTitle(event.target.value)
                          }
                        />
                      )}
                    </Stack>
                  </Grid>

                  <Grid size={12}>
                    <ToggleButtonGroup
                      exclusive
                      value={type}
                      disabled={isEdit}
                      onChange={(_event, value: PromocodeTypeType | null) => {
                        if (value) setType(value);
                      }}
                    >
                      <ToggleButton
                        value={PromocodeTypeType.DiscountType}
                        sx={SELECTED_TOGGLE_SX}
                      >
                        {t("promocodes.form.typeDiscount")}
                      </ToggleButton>
                      <ToggleButton
                        value={PromocodeTypeType.TrialType}
                        sx={SELECTED_TOGGLE_SX}
                      >
                        {t("promocodes.form.typeTrial")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ mt: 0.75 }}
                    >
                      {type === PromocodeTypeType.DiscountType
                        ? t("promocodes.form.typeDiscountNotice")
                        : t("promocodes.form.typeTrialNotice")}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      required
                      fullWidth
                      disabled={isEdit}
                      label={t("promocodes.form.code")}
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t("promocodes.form.limit")}
                      value={limit}
                      onChange={(event) => setLimit(event.target.value)}
                      slotProps={{ htmlInput: { min: 0 } }}
                    />
                  </Grid>

                  {type === PromocodeTypeType.DiscountType ? (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          type="number"
                          required={!amount}
                          label={t("promocodes.form.discount")}
                          value={discount}
                          onChange={(event) => {
                            const raw = event.target.value;
                            if (raw === "") {
                              setDiscount("");
                              return;
                            }
                            setDiscount(
                              String(Math.max(0, Math.min(100, Number(raw))))
                            );
                          }}
                          slotProps={{ htmlInput: { min: 0, max: 100 } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          type="number"
                          required={!discount}
                          label={t("promocodes.form.amount")}
                          value={amount}
                          onChange={(event) => setAmount(event.target.value)}
                          slotProps={{ htmlInput: { min: 0 } }}
                        />
                      </Grid>
                    </>
                  ) : (
                    <Grid size={12}>
                      <TextField
                        fullWidth
                        required
                        type="number"
                        label={t("promocodes.form.trialPeriod")}
                        value={trialPeriod}
                        onChange={(event) => setTrialPeriod(event.target.value)}
                        slotProps={{
                          htmlInput: { min: 0, max: 100 },
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">
                                {t("promocodes.form.days", {
                                  count: Number(trialPeriod) || 0,
                                })}
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid>
                  )}

                  <Grid size={12}>
                    <ProductsAutocomplete
                      label={t("promocodes.form.products")}
                      value={productIds}
                      onChange={setProductIds}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Typography color="textSecondary">
                      {t("promocodes.form.period")}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DateTimePicker
                      label={t("promocodes.form.startAt")}
                      value={startAt}
                      onChange={setStartAt}
                      slotProps={{
                        textField: { required: true, fullWidth: true },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DateTimePicker
                      label={t("promocodes.form.finishAt")}
                      value={finishAt}
                      onChange={setFinishAt}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
    </form>
  );
};
